import { usePaymetricFrame } from "@insite/client-framework/Common/Hooks/usePaymetricFrame";
import { BillingAddress, usePaystandFrame } from "@insite/client-framework/Common/Hooks/usePaystandFrame";
import { SpreedlyIframeStyles, useSpreedlyFrame } from "@insite/client-framework/Common/Hooks/useSpreedlyFrame";
import { useSquareFrame } from "@insite/client-framework/Common/Hooks/useSquareFrame";
import {
    IFrame,
    TokenEx as TokenExType,
    TokenExIframeStyles,
    useTokenExFrame,
} from "@insite/client-framework/Common/Hooks/useTokenExFrame";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasOnError, makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getAdyenSessionData, postAdyenRefund } from "@insite/client-framework/Services/PaymentService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadAdyenSettings from "@insite/client-framework/Store/Context/Handlers/LoadAdyenSettings";
import loadPaymetricConfig from "@insite/client-framework/Store/Context/Handlers/LoadPaymetricConfig";
import loadSpreedlyConfig from "@insite/client-framework/Store/Context/Handlers/LoadSpreedlyConfig";
import loadTokenExConfig from "@insite/client-framework/Store/Context/Handlers/LoadTokenExConfig";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import resetCurrentCartId from "@insite/client-framework/Store/Data/Carts/Handlers/ResetCurrentCartId";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import {
    getCurrentPromotionsDataView,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import checkoutWithPayPal from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/CheckoutWithPayPal";
import getPaymetricResponsePacket from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/GetPaymetricResponsePacket";
import loadAdyenDropInConfig from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/LoadAdyenDropInConfiguration";
import placeOrder from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import setCheckoutPaymentMethod from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetCheckoutPaymentMethod";
import setPaymetricIFrameIsSet from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetPaymetricIFrameIsSet";
import setPlaceOrderErrorMessage from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetPlaceOrderErrorMessage";
import validateAdyenPayment, {
    ValidatAdyenPaymenteResult,
    ValidateAdyenPaymentParameter,
} from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ValidateAdyenPayment";
import preloadOrderConfirmationData from "@insite/client-framework/Store/Pages/OrderConfirmation/Handlers/PreloadOrderConfirmationData";
import translate from "@insite/client-framework/Translate";
import { PaymentMethodDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import usePaymentDetailsCartState from "@insite/content-library/Hooks/Payments/usePaymentDetailsCartState";
import usePaymentDetailsFormHandlers from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormHandlers";
import usePaymentDetailsFormState from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormState";
import usePaymentDetailsFormValidation from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormValidation";
import AdyenDropIn from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/AdyenDropIn";
import CreditCardBillingAddressEntry, {
    CreditCardBillingAddressEntryStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CreditCardBillingAddressEntry";
import CreditCardDetailsEntry, {
    CreditCardDetailsEntryStyles,
    CreditCardDetailsValidation,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CreditCardDetailsEntry";
import ECheckDetailsEntry, {
    ECheckDetailsEntryStyles,
    Validatable,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/ECheckDetailsEntry";
import GooglePayButtonWrapper, {
    GooglePayButtonWrapperStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/GooglePayButtonWrapper";
import ApplePayButtonWrapper, {
    ApplePayButtonWrapperStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/ApplePayButtonWrapper";
import PaymentProfileBillingAddress from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/PaymentProfileBillingAddress";
import PayPalButton, { PayPalButtonStyles } from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/PayPalButton";
import SavedPaymentProfileEntry, {
    SavedPaymentProfileEntryStyles,
    SavedPaymentProfileValidation,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/SavedPaymentProfileEntry";
import { convertPaymetricCardType } from "@insite/content-library/Widgets/SavedPayments/PaymentUtilities";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { generateSpreedlyFrameStyleConfig } from "@insite/mobius/SpreedlyFrame";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { generateTokenExFrameStyleConfig } from "@insite/mobius/TokenExFrame";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { isZeroTotalCheckout, resolveCheckoutPaymentMethod } from "@tSurya/Common/Utilities/checkoutPaymentMethod";
import { getIsUkWebsite, isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import {
    getPaymentBreakdown,
    getShippingPromotionTotal,
    hasMgbwTermsProperty,
    isTermsPaymentMethod,
} from "@tSurya/Common/Utilities/paymentBreakdown";
import CheckoutBillingAddressCard from "@tSurya/Components/CheckoutBillingAddressCard";
import CheckoutSectionHeader from "@tSurya/Components/CheckoutSectionHeader";
import { useSurCheckoutPayment } from "@tSurya/Components/SurCheckoutPaymentContext";
import SurPaymentBreakdown from "@tSurya/Components/SurPaymentBreakdown";
import { CHECKOUT_WIZARD_STEP, useSurCheckoutWizard } from "@tSurya/Components/SurCheckoutWizardContext";
import { WIDGETS } from "@tSurya/theme/design";
import { COLORS } from "@tSurya/theme/design.surya";
import { PlaceOrderParameter } from "@tSurya/Handlers/CheckoutReviewAndSubmit/PlaceOrderExtended";
import qs from "qs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const checkoutPaymentInformationTokens = (WIDGETS as any).checkoutPaymentInformation;

const mapStateToProps = (state: ApplicationState) => {
    const {
        cartId,
        payPalRedirectUri,
        payPalCheckoutErrorMessage,
        placeOrderErrorMessage,
        requestedPickUpDateDisabled,
        requestedDeliveryDateDisabled,
        paymetricIFrameIsSet,
    } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);
    const settingsCollection = getSettingsCollection(state);
    return {
        cartState,
        shippingPromotionTotal: getShippingPromotionTotal(promotionsDataView.value),
        billToState: getBillToState(state, cartState.value ? cartState.value.billToId : undefined),
        countries: getCurrentCountries(state),
        websiteSettings: settingsCollection.websiteSettings,
        cartSettings: settingsCollection.cartSettings,
        tokenExConfigs: state.context.tokenExConfigs,
        orderConfirmationPageLink: getPageLinkByPageType(state, "OrderConfirmationPage"),
        savedPaymentsPageLink: getPageLinkByPageType(state, "SavedPaymentsPage"),
        session: state.context.session,
        signInPageLink: getPageLinkByPageType(state, "SignInPage"),
        checkoutReviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
        payPalRedirectUri,
        payPalCheckoutErrorMessage,
        location: getLocation(state),
        adyenSettings: state.context.adyenSettings,
        paymetricConfig: state.context.paymetricConfig,
        enableVat: settingsCollection.productSettings.enableVat,
        bypassCvvForSavedCards: settingsCollection.cartSettings.bypassCvvForSavedCards,
        placeOrderErrorMessage,
        requestedDeliveryDateDisabled,
        requestedPickUpDateDisabled,
        spreedlyConfig: state.context.spreedlyConfig,
        currency: state.context.session.currency,
        paymetricIFrameIsSet,
        antiFraudSessionId: state.context.stripeRadarSessionId ?? "",
        isStripeRadarScriptLoaded: state.context.stripeRadarSessionId !== undefined,
        isUkWebsite: getIsUkWebsite(state),
    };
};

const mapDispatchToProps = {
    loadTokenExConfig,
    placeOrder,
    checkoutWithPayPal,
    preloadOrderConfirmationData,
    loadBillTo,
    loadPaymetricConfig,
    loadAdyenSettings,
    loadAdyenDropInConfig,
    loadSpreedlyConfig,
    getPaymetricResponsePacket,
    resetCurrentCartId,
    setCheckoutPaymentMethod,
    signOut,
    validateAdyenPayment: makeHandlerChainAwaitable<ValidateAdyenPaymentParameter, ValidatAdyenPaymenteResult>(
        validateAdyenPayment,
    ),
    setPlaceOrderErrorMessage,
    setPaymetricIFrameIsSet,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasHistory &
    HasToasterContext;

export interface CheckoutReviewAndSubmitPaymentDetailsStyles {
    form?: InjectableCss;
    fieldset?: InjectableCss;
    cardContentGridItem?: GridItemProps;
    cardContent?: InjectableCss;
    paymentDetailsHeading?: TypographyPresentationProps;
    paymentMethodPayPalText?: TypographyPresentationProps;
    termsPaymentMethod?: InjectableCss;
    termsPaymentMethodLabel?: TypographyPresentationProps;
    termsPaymentMethodValue?: TypographyPresentationProps;
    termsPaymentMethodHelper?: TypographyPresentationProps;
    paymentMethodAndPONumberContainer?: GridContainerProps;
    paymentMethodGridItem?: GridItemProps;
    paymentMethodSelect?: SelectPresentationProps;
    paymentProfileBillingAddress?: any;
    paymentProfileExpiredErrorWrapper?: InjectableCss;
    paymentProfileExpiredErrorText?: TypographyPresentationProps;
    paymentProfileEditCardLink?: LinkPresentationProps;
    vatNumberGridItem?: GridItemProps;
    vatNumberText?: TextFieldPresentationProps;
    poNumberGridItem?: GridItemProps;
    poNumberText?: TextFieldPresentationProps;
    emptyGridItem?: GridItemProps;
    adyenDropInGridItem?: GridItemProps;
    mainContainer?: GridContainerProps;
    savedPaymentProfile?: SavedPaymentProfileEntryStyles;
    creditCardDetailsGridItem?: GridItemProps;
    paystandDetailsGridItem?: GridItemProps;
    eCheckDetailsGridItem?: GridItemProps;
    creditCardDetails?: CreditCardDetailsEntryStyles;
    creditCardAddressGridItem?: GridItemProps;
    creditCardAddress?: CreditCardBillingAddressEntryStyles;
    payPalButton?: PayPalButtonStyles;
    eCheckDetailsEntryStyles?: ECheckDetailsEntryStyles;
    paypalCheckoutErrorWrapper?: InjectableCss;
    paypalCheckoutErrorText?: TypographyPresentationProps;
    loadingSpinner?: LoadingSpinnerProps;
    shipContainer?: InjectableCss;
    shipTitle?: TypographyPresentationProps;
    paymentBilling?: InjectableCss;
    externalPayButtonsContainer?: InjectableCss;
    googlePayButtonWrapperStyles?: GooglePayButtonWrapperStyles;
    applePayButtonWrapperStyles?: ApplePayButtonWrapperStyles;
    externalProviderCreditCardInfoText?: TypographyPresentationProps;
}

export const checkoutReviewAndSubmitPaymentDetailsStyles: CheckoutReviewAndSubmitPaymentDetailsStyles = {
    fieldset: {
        css: css`
            margin: 0;
            padding: 0;
            border: 0;
        `,
    },
    externalPayButtonsContainer: {
        css: css`
            display: flex;
            gap: 10px;
            align-items: stretch;
            padding-top: 1em;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: row;
                    `,
                    css`
                        flex-direction: row;
                    `,
                    css`
                        flex-direction: row;
                    `,
                    css`
                        flex-direction: row;
                    `,
                ])}
        `,
    },
    googlePayButtonWrapperStyles: {
        container: {
            css: css`
                padding-top: 0;
                width: 240px;
            `,
        },
        button: { width: "240px" },
    },
    applePayButtonWrapperStyles: {
        container: {
            css: css`
                padding-top: 0;
            `,
        },
        button: {
            css: css`
                position: relative;

                --apple-pay-button-width: 240px;
                --apple-pay-button-height: 40px;
                --apple-pay-button-border-radius: 4px;
                --apple-pay-button-padding: 0px 0px;
                --apple-pay-button-box-sizing: border-box;
            `,
        },
    },
    payPalButton: {
        container: {
            css: css`
                padding-top: 1em;
                width: 240px;
            `,
        },
    },
    cardContentGridItem: {
        width: 12,
        css: css`
            display: block;
            padding: 0;
        `,
    },
    cardContent: {
        css: css`
            background-color: ${COLORS.white};
            border: 1px solid ${COLORS.border.main};
            border-radius: 4px;
            padding: 10px 14px;
        `,
    },
    paymentDetailsHeading: { variant: "h5" },
    termsPaymentMethod: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            gap: ${checkoutPaymentInformationTokens?.termsMethod?.gap};
            padding: ${checkoutPaymentInformationTokens?.termsMethod?.padding};
            background-color: ${checkoutPaymentInformationTokens?.termsMethod?.background};
            border: ${checkoutPaymentInformationTokens?.termsMethod?.border};
            border-radius: ${checkoutPaymentInformationTokens?.termsMethod?.borderRadius};
        `,
    },
    termsPaymentMethodLabel: {
        css: css`
            color: ${checkoutPaymentInformationTokens?.termsMethodLabel?.color};
            font-size: ${checkoutPaymentInformationTokens?.termsMethodLabel?.fontSize};
            font-weight: ${checkoutPaymentInformationTokens?.termsMethodLabel?.fontWeight};
            line-height: ${checkoutPaymentInformationTokens?.termsMethodLabel?.lineHeight};
        `,
    },
    termsPaymentMethodValue: {
        css: css`
            color: ${checkoutPaymentInformationTokens?.termsMethodValue?.color};
            font-size: ${checkoutPaymentInformationTokens?.termsMethodValue?.fontSize};
            font-weight: ${checkoutPaymentInformationTokens?.termsMethodValue?.fontWeight};
            line-height: ${checkoutPaymentInformationTokens?.termsMethodValue?.lineHeight};
        `,
    },
    termsPaymentMethodHelper: {
        css: css`
            color: ${checkoutPaymentInformationTokens?.termsMethodHelper?.color};
            font-size: ${checkoutPaymentInformationTokens?.termsMethodHelper?.fontSize};
            line-height: ${checkoutPaymentInformationTokens?.termsMethodHelper?.lineHeight};
        `,
    },
    paymentMethodAndPONumberContainer: {
        gap: 0,
        css: css`
            [class^="GridItemStyle"] {
                padding: 10px !important;

                input, select {
                    height: 48px !important;
                    font-size: 16px !important;
                    color: ${COLORS.checkout.text.secondary} !important;
                }
            }
        `
    },
    paymentMethodGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            padding: 24px;
        `,
    },
    paymentProfileExpiredErrorWrapper: {
        css: css`
            display: flex;
            width: 100%;
            margin-top: 15px;
        `,
    },
    paymentBilling: {
        css: css`
            margin-top: 15px;
        `,
    },
    paymentProfileExpiredErrorText: { color: "danger" },
    paymentProfileEditCardLink: {
        css: css`
            margin-left: 1rem;
        `,
    },
    vatNumberGridItem: { width: 6 },
    poNumberGridItem: { width: 6 },
    emptyGridItem: { width: 6 },
    creditCardDetailsGridItem: { width: [12, 12, 12, 6, 6] },
    paystandDetailsGridItem: { width: 12 },
    eCheckDetailsGridItem: { width: [12, 12, 12, 6, 6] },
    creditCardAddressGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
            padding: 24px;
        `,
    },
    paypalCheckoutErrorWrapper: {
        css: css`
            display: flex;
            width: 100%;
            padding-top: 1em;
        `,
    },
    paypalCheckoutErrorText: { color: "danger" },
    loadingSpinner: {
        css: css`
            margin: auto;
        `,
    },
    adyenDropInGridItem: { width: 12 },
    shipContainer: {
        css: css`
            margin: 10px 0 20px;
            [class^="CheckboxStyle"] span {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-top: -49px;
                            `,
                            css`
                                margin-top: -24px;
                            `,
                            css`
                                margin-top: -45px;
                            `,
                            css`
                                margin-top: 0px;
                            `,
                            css`
                                margin-top: 0px;
                            `,
                        ],
                        "min",
                    )}
            }
        `,
    },
    shipTitle: {
        css: css`
            margin-bottom: 0;
        `,
    },
};

declare const TokenEx: TokenExType;
let tokenExIframe: IFrame | undefined;
let tokenExAccountNumberIframe: IFrame | undefined;
let tokenExFrameStyleConfig: TokenExIframeStyles;
let spreedlyFrameStyleConfig: SpreedlyIframeStyles;

const isNonEmptyString = (value: string | undefined) => value !== undefined && value.trim() !== "";

const isMonthAndYearBeforeToday = (month: number, year: number) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    return year < currentYear || (year === currentYear && month < currentMonth + 1);
};

const styles = checkoutReviewAndSubmitPaymentDetailsStyles;
const StyledForm = getStyledWrapper("form");
const StyledFieldSet = getStyledWrapper("fieldset");

const CheckoutReviewAndSubmitPaymentDetails = ({
    loadBillTo,
    cartState,
    billToState,
    tokenExConfigs,
    placeOrder,
    orderConfirmationPageLink,
    savedPaymentsPageLink,
    history,
    checkoutWithPayPal,
    payPalRedirectUri,
    payPalCheckoutErrorMessage,
    preloadOrderConfirmationData,
    loadTokenExConfig,
    theme,
    session,
    checkoutReviewAndSubmitPageLink,
    location,
    toaster,
    paymetricConfig,
    loadPaymetricConfig,
    adyenSettings,
    loadAdyenSettings,
    loadAdyenDropInConfig,
    loadSpreedlyConfig,
    spreedlyConfig,
    getPaymetricResponsePacket,
    resetCurrentCartId,
    enableVat,
    bypassCvvForSavedCards,
    setCheckoutPaymentMethod,
    signInPageLink,
    placeOrderErrorMessage,
    signOut,
    requestedDeliveryDateDisabled,
    requestedPickUpDateDisabled,
    shippingPromotionTotal,
    validateAdyenPayment,
    setPlaceOrderErrorMessage,
    setPaymetricIFrameIsSet,
    currency,
    antiFraudSessionId,
    isStripeRadarScriptLoaded,
    isUkWebsite,
}: Props) => {
    // This is available before the selected-method resolver runs, including for Adyen
    // redirects that must return to the card flow.
    const {
        PayerID: payPalPayerId,
        token: payPalToken,
        redirectResult: adyenRedirectResult,
        cartId,
    } = parseQueryString<{
        PayerID?: string;
        token?: string;
        redirectResult?: string;
        cartId?: string;
    }>(location.search);
    const paymentDetailsFormState = usePaymentDetailsFormState();
    const {
        paymentMethod,
        setPaymentMethod,
        poNumber,
        vatNumber,
        saveCard,
        cardHolderName,
        setCardHolderName,
        cardNumber,
        setCardNumber,
        cardType,
        setCardType,
        expirationMonth,
        setExpirationMonth,
        expirationYear,
        setExpirationYear,
        securityCode,
        setSecurityCode,
        useBillingAddress,
        setUseBillingAddress,
        address1,
        setAddress1,
        countryId,
        setCountryId,
        stateId,
        setStateId,
        city,
        setCity,
        postalCode,
        setPostalCode,
        paymentMethodError,
        poNumberError,
        address1Error,
        countryError,
        stateError,
        cityError,
        postalCodeError,
        payPalError,
        paystandError,
        setPayPalError,
        setPaystandError,
        showFormErrors,
        setShowFormErrors,
        isCardNumberTokenized,
        setIsCardNumberTokenized,
        isECheckTokenized,
        setIsECheckTokenized,
        accountHolderName,
        setAccountHolderName,
        accountNumber,
        setAccountNumber,
        routingNumber,
        setRoutingNumber,
        payerId,
        setPayerId,
        creditCardLastFourDigits,
        setCreditCardLastFourDigits,
        bankAccountLastFourDigits,
        setBankAccountLastFourDigits,
        isIframeLoaded,
        setIsIframeLoaded,
        isPayPal,
        setIsPayPal,
        runSubmitPayPal,
        setRunSubmitPayPal,
        paymentMethodRef,
        poNumberRef,
        address1Ref,
        countryRef,
        stateRef,
        cityRef,
        postalCodeRef,
        maskedCardNumber,
        setMaskedCardNumber,
        googlePayError,
        setGooglePayError,
        applePayError,
        setApplePayError,
        externalTokenProvider,
        setExternalTokenProvider,
    } = paymentDetailsFormState;
    const paymentDetailsCartState = usePaymentDetailsCartState(cartState, paymentMethod, countryId);
    const {
        cart,
        useTokenExGateway,
        useECheckTokenExGateway,
        usePaymetricGateway,
        useSquareGateway,
        useAdyenDropIn,
        usePaystandDropIn,
        countries,
        showPayPal,
        paymentOptions,
        paymentMethods,
        paymentMethodDto,
        isCreditCard,
        isECheck,
        isPaymentProfile,
        selectedCountry,
        websiteSettings,
        useSpreedlyDropIn,
        enableGooglePay,
        enableApplePay,
    } = paymentDetailsCartState;
    const paymentDetailsFormValidation = usePaymentDetailsFormValidation(
        paymentDetailsFormState,
        paymentDetailsCartState,
    );
    const {
        validatePaymentMethod,
        validatePONumber,
        validateAddress1,
        validateCountry,
        validateState,
        validateCity,
        validatePostalCode,
    } = paymentDetailsFormValidation;
    const {
        handlePaymentMethodChange,
        handlePONumberChange,
        handleVatNumberChange,
        handleSaveCardChange,
        handleCardHolderNameChange,
        handleCardNumberChange,
        handleCardTypeChange,
        handleExpirationMonthChange,
        handleExpirationYearChange,
        handleSecurityCodeChange,
        handleUseBillingAddressChange,
        handleAddressChange,
        handleCountryChange,
        handleStateChange,
        handleCityChange,
        handlePostalCodeChange,
        handleEditCardClick,
    } = usePaymentDetailsFormHandlers({
        paymentDetailsFormState,
        paymentDetailsFormValidation,
        paymentDetailsCartState,
        history,
        savedPaymentsPageLink,
        setPaymentMethodCallback: (
            event: React.ChangeEvent<HTMLSelectElement>,
            paymentMethods: PaymentMethodDto[] | null | undefined,
        ) => {
            setCheckoutPaymentMethod({
                paymentMethod: paymentMethods?.find(method => method.name === event.target.value),
            });
        },
    });
    const [possibleCardType, setPossibleCardType] = useState("");
    const [cardHolderNameError, setCardHolderNameError] = useState("");
    const [cardNumberError, setCardNumberError] = useState("");
    const [cardTypeError, setCardTypeError] = useState("");
    const [securityCodeError, setSecurityCodeError] = useState("");
    const [expirationError, setExpirationError] = useState("");
    const [poRequired, setPorequired] = useState(true);
    const { paymentSplitChoice, setPaymentSplitChoice } = useSurCheckoutPayment();

    // Unified checkout Payment Breakdown (SR-1544). Applied to all websites (Surya, Global
    // Views, MGBW) for a consistent UI. The NET30/terms method (detected by capability, not by
    // name) is chosen via the Payment Breakdown section, so it is removed from the payment
    // method dropdown.
    const userHasTerms = !!paymentMethods?.some(isTermsPaymentMethod);
    const visiblePaymentMethods = useMemo(
        () => (paymentMethods ?? []).filter(method => !isTermsPaymentMethod(method)),
        [paymentMethods],
    );
    const termsMethod = useMemo(() => paymentMethods?.find(isTermsPaymentMethod), [paymentMethods]);

    // Terms-for-all (SR-1544 extension): the bill-to has an MGBW term code (customer custom
    // property synced from AX), so the MG+BW portion is terms-eligible too and the whole order
    // can be placed on terms with no forced MGBW prepay split.
    const userHasMgbwTerms = hasMgbwTermsProperty(billToState.value?.properties);

    // The effective split choice mirrors SurPaymentBreakdown's normalization: when the
    // stored choice is not valid for the current cart/scenario (cart contents changed),
    // fall back to the scenario default so an invalid choice is never submitted.
    const breakdownModel = getPaymentBreakdown(cart, userHasTerms, userHasMgbwTerms, shippingPromotionTotal);
    const effectiveSplitChoice = breakdownModel.availableChoices.includes(paymentSplitChoice)
        ? paymentSplitChoice
        : breakdownModel.defaultChoice;
    const isPayOnTerms = effectiveSplitChoice === "payAllOnTerms";
    const isZeroTotal = isZeroTotalCheckout(cart?.orderGrandTotal);

    // Keep the hidden payment method in local form state while the shopper compares Payment
    // Options. The final Place Order request persists this method with paymentSplitChoice,
    // avoiding a cart PUT and reload for each radio toggle.
    useEffect(() => {
        const resolvedPaymentMethod = resolveCheckoutPaymentMethod({
            currentPaymentMethod: paymentMethod,
            persistedPaymentMethod: cartState.value?.paymentMethod,
            visiblePaymentMethods,
            termsMethod,
            // An Adyen redirect must return to the card flow even when a terms option is the
            // default for the refreshed checkout page.
            isPayOnTerms: isPayOnTerms && !(useAdyenDropIn && adyenRedirectResult),
        });

        if (resolvedPaymentMethod !== paymentMethod) {
            setPaymentMethod(resolvedPaymentMethod);
        }
    }, [
        cartState.value?.paymentMethod?.name,
        adyenRedirectResult,
        isPayOnTerms,
        paymentMethod,
        termsMethod,
        useAdyenDropIn,
        visiblePaymentMethods,
    ]);

    // Wizard integration: report reactive validity so the Next button on the
    // payment step is disabled until the required fields are filled, register a
    // (side-effect-safe) field validator for the submit-time jump, and expose
    // goToStep so a failed submit can surface this step.
    const { setStepValidity, registerStepValidator, goToStep } = useSurCheckoutWizard();
    const validatePaymentFieldsRef = useRef<() => boolean | Promise<boolean>>(() => true);
    useEffect(
        () => registerStepValidator(CHECKOUT_WIZARD_STEP.Payment, () => validatePaymentFieldsRef.current()),
        [registerStepValidator],
    );

    // Pure (no side effects) mirror of the required-field checks used to gate the
    // Next button reactively. Deep card/gateway validation runs on submit.
    const isPaymentStepValid =
                isZeroTotal
                        ? (!cart?.showPoNumber || !cart.requiresPoNumber || isNonEmptyString(poNumber)) &&
                            !requestedDeliveryDateDisabled &&
                            !requestedPickUpDateDisabled
                        :
        (isPayPal || !paymentMethods || paymentMethods.length === 0 || isNonEmptyString(paymentMethod)) &&
        (!cart || !cart.showPoNumber || !cart.requiresPoNumber || isNonEmptyString(poNumber)) &&
        (!(isCreditCard || isECheck) ||
            useBillingAddress ||
            (isNonEmptyString(address1) &&
                isNonEmptyString(countryId) &&
                ((!!selectedCountry && !!selectedCountry.states && selectedCountry.states.length <= 0) ||
                    isNonEmptyString(stateId)) &&
                isNonEmptyString(city) &&
                isNonEmptyString(postalCode)));
    useEffect(() => {
        setStepValidity(CHECKOUT_WIZARD_STEP.Payment, isPaymentStepValid);
    }, [isPaymentStepValid, setStepValidity]);

    const cardHolderNameRef = useRef<HTMLInputElement>(null);
    const cardNumberRef = useRef<HTMLInputElement>(null);
    const cardTypeRef = useRef<HTMLSelectElement>(null);
    const securityCodeRef = useRef<HTMLInputElement>(null);
    const expirationMonthRef = useRef<HTMLSelectElement>(null);
    const expirationYearRef = useRef<HTMLSelectElement>(null);
    const tokenExLibraryIsLoaded = useTokenExFrame();
    const spreedlyLibraryIsLoaded = useSpreedlyFrame();

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && cartState.value && cartState.value.billToId) {
            loadBillTo({ billToId: cartState.value.billToId });
        }
    }, [billToState]);

      useEffect(() => {
        if (payPalCheckoutErrorMessage) {
            setPayPalError(payPalCheckoutErrorMessage);
            setIsPayPal(false);
            setRunSubmitPayPal(false);
        }
    }, [payPalCheckoutErrorMessage]);

    useEffect(() => {
        if (showFormErrors) {
            scrollToFirstFormError();
        }
    }, [
        showFormErrors,
        paymentMethodRef.current,
        poNumberRef.current,
        cardHolderNameRef.current,
        cardNumberRef.current,
        cardTypeRef.current,
        expirationYearRef.current,
        expirationMonthRef.current,
        securityCodeRef.current,
        address1Ref.current,
        countryRef.current,
        stateRef.current,
        cityRef.current,
        postalCodeRef.current,
    ]);

    useEffect(() => {
        if (isCardNumberTokenized || isECheckTokenized) {
            placeOrder({
                paymentMethod,
                poNumber,
                vatNumber,
                saveCard,
                cardHolderName,
                cardNumber,
                cardType,
                expirationMonth,
                expirationYear,
                securityCode,
                useBillingAddress,
                address1,
                countryId,
                stateId,
                city,
                postalCode,
                payPalToken,
                payPalPayerId,
                accountHolderName,
                accountNumber,
                routingNumber,
                payerId,
                maskedCardNumber,
                paymentSplitChoice: effectiveSplitChoice,
                antiFraudSessionId,
                onSuccess: (cartId: string) => {
                    if (!orderConfirmationPageLink) {
                        return;
                    }

                    if (cart?.isAwaitingApproval) {
                        resetCurrentCartId({});
                        toaster.addToast({
                            body: siteMessage("OrderApproval_OrderPlaced"),
                            messageType: "success",
                        });
                    }

                    history.push(`${orderConfirmationPageLink.url}?cartId=${cartId}`);
                },
                onError: () => {
                    setIsCardNumberTokenized(false);
                    tokenExIframe?.reset();
                    if (usePaymetricGateway) {
                        // this will end up resetting the paymetric iframe
                        loadPaymetricConfig();
                    }
                },
                onComplete(resultProps: any) {
                    if (resultProps.apiResult?.cart) {
                        // "this" is targeting the object being created, not the parent SFC
                         
                        this.onSuccess?.(resultProps.apiResult.cart.id);
                    } else {
                        // "this" is targeting the object being created, not the parent SFC
                         
                        this.onError?.();
                    }
                },
            } as PlaceOrderParameter & HasOnError);
        }
    }, [isCardNumberTokenized, isECheckTokenized]);

    // IsPayPal
    // Setup isPayPal from cart.paymentOptions and validates form when cartState changes and is loaded.
    useEffect(() => {
        if (cartState.value) {
            const tempIsPayPal = cartState.value.paymentOptions?.isPayPal || (!!payPalToken && !!payPalPayerId);
            setIsPayPal(tempIsPayPal);
            if (tempIsPayPal) {
                void validateForm();
            }
        }
    }, [cartState]);

    // Submit PayPal
    // When isPayPal and runSubmitPayPal are true will validate form, and submitPayPal with the current page redirectUri and current cart.
    useEffect(() => {
        if (isPayPal && runSubmitPayPal) {
            (async () => {
                if (!(await validateForm())) {
                    setShowFormErrors(true);
                    return;
                }
                if (!checkoutReviewAndSubmitPageLink) {
                    return;
                }
                let redirectUri = `${window.location.host}${checkoutReviewAndSubmitPageLink.url}`;
                if (cartId) {
                    redirectUri += `?cartId=${cartId}`;
                }
                checkoutWithPayPal({ redirectUri });
            })();
        }
    }, [runSubmitPayPal, isPayPal]);

    // Submit PayPal State Check
    // Handles the PayPal button click response, getting the payPal redirectUri from the server on cart update call.
    useEffect(() => {
        if (payPalRedirectUri) {
            window.location.href = payPalRedirectUri;
        }
    }, [payPalRedirectUri]);

    const [adyenSessionId, setAdyenSessionId] = useState("");
    const [adyenSessionData, setAdyenSessionData] = useState("");
    const [adyenWebOrderNumber, setAdyenWebOrderNumber] = useState("");
    const [adyenRegion, setAdyenRegion] = useState("");
    const [adyenErrorMessage, setAdyenErrorMessage] = useState("");
    const [adyenConfiguration, setAdyenConfiguration] = useState<any>(null);
    const [adyenDropIn, setAdyenDropIn] = useState<any>(null);
    const isPaymentProfileExpired = paymentMethodDto?.isPaymentProfileExpired === true;
    let tokenName: string | undefined;
    const eCheckDetails = useRef<Validatable>(null);
    const creditCardDetails = useRef<CreditCardDetailsValidation>(null);
    const savedPaymentProfile = useRef<SavedPaymentProfileValidation>(null);
    const tokenExReinitTimeout = 500;
    let lastTokenExReinitTime = 0;
    const tokenExErrorsLimit = (20 * 1e3) / tokenExReinitTimeout;
    let currentTokenExErrorCount = 0;

    if (useTokenExGateway) {
        if (isPaymentProfile) {
            tokenName = paymentMethodDto!.name;
        } else if (isCreditCard) {
            tokenName = "";
        }
    }

    if (useECheckTokenExGateway) {
        if (isECheck) {
            tokenName = "";
        }
    }

    const placeAdyenOrder = async (paymentResult: string) => {
        const { success, adyenPspReference, errorMessage, paymentAmount, currency } = await validateAdyenPayment({
            paymentResult,
        });
        if (!success) {
            if (adyenPspReference && paymentAmount && currency) {
                postAdyenRefund(adyenPspReference, adyenWebOrderNumber, paymentAmount, currency);
            }

            if (errorMessage) {
                setAdyenErrorMessage(errorMessage);
            }

            resetAdyenSession();
            return;
        }

        placeOrder({
            paymentMethod: "",
            poNumber: window.localStorage.getItem("order-po-number") ?? poNumber,
            vatNumber,
            saveCard,
            cardHolderName,
            cardNumber,
            cardType,
            expirationMonth,
            expirationYear,
            securityCode,
            useBillingAddress,
            address1,
            countryId,
            stateId,
            city,
            postalCode,
            payPalToken,
            payPalPayerId,
            isPending: true,
            isAdyenDropIn: true,
            adyenPspReference,
            accountHolderName,
            accountNumber,
            routingNumber,
            payerId,
            maskedCardNumber,
            paymentSplitChoice: effectiveSplitChoice,
            antiFraudSessionId,
            onSuccess: (cartId: string) => {
                preloadOrderConfirmationData({
                    cartId,
                    onSuccess: () => {
                        if (cart?.isAwaitingApproval) {
                            resetCurrentCartId({});
                            toaster.addToast({
                                body: siteMessage("OrderApproval_OrderPlaced"),
                                messageType: "success",
                            });
                        }
                        history.push(`${orderConfirmationPageLink!.url}?cartId=${cartId}`);
                    },
                });
            },
            onComplete(resultProps: any) {
                if (!resultProps.apiResult?.cart) {
                    return;
                }
                // "this" is targeting the object being created, not the parent SFC
                 
                this.onSuccess?.(resultProps.apiResult.cart.id);
            },
        } as PlaceOrderParameter);
    };

    const resetAdyenSession = () => {
        setAdyenSessionId("");
        setAdyenSessionData("");
        setAdyenRegion("");
        setAdyenConfiguration(null);

        const parsedQuery = parseQueryString<any>(window.location.search);
        delete parsedQuery.customerId;
        delete parsedQuery.amount;
        delete parsedQuery.sessionId;
        delete parsedQuery.redirectResult;
        delete parsedQuery.type;
        delete parsedQuery.resultCode;
        const queryString = qs.stringify(parsedQuery);
        history.replace(`${window.location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
    };

    useEffect(() => {
        let mounted = true;
        if (
            useAdyenDropIn &&
            paymentMethodDto?.isCreditCard &&
            checkoutReviewAndSubmitPageLink &&
            cartState.value?.orderGrandTotal &&
            !adyenSessionData
        ) {
            if (!adyenSettings?.clientKey) {
                loadAdyenSettings();
            }
            const returnUrl = !cartId
                ? checkoutReviewAndSubmitPageLink.url
                : checkoutReviewAndSubmitPageLink.url.includes("?")
                ? `${checkoutReviewAndSubmitPageLink.url}&cartId=${cartId}`
                : `${checkoutReviewAndSubmitPageLink.url}?cartId=${cartId}`;
            getAdyenSessionData(cartState.value.orderGrandTotal, returnUrl, cartId).then(result => {
                if (!mounted) {
                    return;
                }
                setAdyenSessionId(result.transactionId);
                setAdyenSessionData(result.sessionData);
                setAdyenWebOrderNumber(result.webOrderNumber);
                setAdyenRegion(result.region);
            });
        }
        return () => {
            mounted = false;
        };
    }, [useAdyenDropIn, checkoutReviewAndSubmitPageLink, cartState, paymentMethodDto, adyenSessionData]);

    useEffect(() => {
        if (adyenSessionId && adyenSessionData && adyenRegion && adyenSettings?.clientKey && !adyenConfiguration) {
            loadAdyenDropInConfig({
                sessionId: adyenSessionId,
                sessionData: adyenSessionData,
                region: adyenRegion,
                clientKey: adyenSettings.clientKey,
                cartId,
                webOrderNumber: adyenWebOrderNumber,
                returnUrl: !cartId
                    ? checkoutReviewAndSubmitPageLink!.url
                    : checkoutReviewAndSubmitPageLink!.url.includes("?")
                    ? `${checkoutReviewAndSubmitPageLink!.url}&cartId=${cartId}`
                    : `${checkoutReviewAndSubmitPageLink!.url}?cartId=${cartId}`,
                setAdyenErrorMessage,
                resetAdyenSession,
                placeAdyenOrder,
                onSuccess: (adyenConfiguration: any) => {
                    setAdyenConfiguration(adyenConfiguration);
                },
            });
        }
    }, [adyenSessionId, adyenSessionData, adyenRegion, adyenSettings]);

    useEffect(() => {
        setAdyenSessionId("");
        setAdyenSessionData("");
        setAdyenRegion("");
        setAdyenConfiguration(null);
    }, [cartState.value?.billToId]);

    useEffect(() => {
        if (typeof tokenName !== "undefined") {
            loadTokenExConfig({ token: tokenName, isECheck });
        }
    }, [paymentMethod]);

    useEffect(() => {
        if (useSpreedlyDropIn && !spreedlyConfig?.environmentKey) {
            loadSpreedlyConfig();
        }
    }, [paymentMethod]);

    const handleTokenExSessionExpired = (token: string) => {
        loadTokenExConfig({ token, isECheck });
    };

    const tokenExConfig = typeof tokenName !== "undefined" ? tokenExConfigs[tokenName] : undefined;

    const [paymetricFrameRef, paymetricIframe] = usePaymetricFrame(
        usePaymetricGateway,
        paymentMethod,
        isCreditCard,
        paymetricConfig,
        loadPaymetricConfig,
        setPaymetricIFrameIsSet,
    );

    const [paystandCard] = usePaystandFrame(websiteSettings, paymentMethod, isCreditCard);
    useEffect(() => {
        if (paystandCard.getIsPaystandScriptLoaded() && billToState.value) {
            paystandCard.setup(
                billToState.value,
                () => {
                    setIsIframeLoaded(true);
                },
                data => {
                    const isBillToUpdated = updateBillToCallback(
                        data.card?.billingAddress ?? data.bank?.billingAddress,
                    );
                    if (isBillToUpdated) {
                        toaster.addToast({
                            body: translate("Payment method submitted successfully"),
                            messageType: "success",
                        });
                        setCardNumber(data.id);
                        setPayerId(data.payerId);
                        setBankAccountLastFourDigits(data.bank?.last4);
                        setPaystandError("");
                        setCardHolderName(data.card?.nameOnCard ?? data?.bank.nameOnAccount);
                        setCardType(data.card?.brand.toUpperCase() ?? data.bank?.bankName);
                        if (data.card) {
                            setExpirationMonth(parseInt(data.card.expirationMonth, 10));
                            setExpirationYear(parseInt(data.card.expirationYear, 10));
                            setCreditCardLastFourDigits(data.card.last4);
                            setMaskedCardNumber(data.card.last4);
                        }
                    } else {
                        setShowFormErrors(true);
                        setPaystandError(
                            translate("Selected country isn't supported. Please re-enter payment details."),
                        );
                    }
                },
            );
        }
    }, [paystandCard.getIsPaystandScriptLoaded(), paymentMethod]);

    const updateBillToCallback = (address: BillingAddress): boolean => {
        const country = countries?.find(
            o =>
                o.name === address.country ||
                o.abbreviation === address.country ||
                o.states?.find(o => o.name === address.state || o.abbreviation === address.state),
        );

        if (country) {
            setCountryId(country.id);
            const state = country.states?.find(o => o.name === address.state || o.abbreviation === address.state);
            if (state) {
                setStateId(state.id);
            }
        } else {
            return false;
        }

        setUseBillingAddress(false);
        setAddress1(address.street1);
        setCity(address.city);
        setPostalCode(address.postalCode);

        return true;
    };

    const [squareCard] = useSquareFrame(websiteSettings, paymentMethod, isCreditCard);

    const tokenizeSquareCard = () => {
        squareCard?.tokenize().then((tokenResult: any) => {
            if (tokenResult.status === "OK") {
                setCardType(tokenResult.details.card.brand);
                setExpirationMonth(tokenResult.details.card.expMonth);
                setExpirationYear(tokenResult.details.card.expYear);
                setCardNumber(tokenResult.token);
                setIsCardNumberTokenized(true);
            }
        });
    };

    const toasterContext = React.useContext(ToasterContext);
    useEffect(() => {
        if (placeOrderErrorMessage === siteMessage("CreditCardInfo_MaximumCreditCardAttempts")) {
            toasterContext.addToast({
                body: placeOrderErrorMessage,
                messageType: "danger",
            });

            setTimeout(() => {
                signOut();
                signInPageLink && history.push(signInPageLink.url);
            }, 3000);
        }
    }, [placeOrderErrorMessage]);

    // const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    //     if (event.currentTarget.value !== "PayPal") {
    //         setIsPayPal(false);
    //     }
    //     // if (event.currentTarget.value === "Credit Card" || /^\d{16}$/.test(event.currentTarget.value)) {
    //     //     setPorequired(false);
    //     //     setPONumberError("");
    //     // } else {
    //     //     setPorequired(true);
    //     //     setPONumberError("");
    //     // }
    //     setPaymentMethod(event.currentTarget.value);
    //     validatePaymentMethod(event.currentTarget.value);

    //     setCheckoutPaymentMethod({ paymentMethod: paymentMethods?.find(method => method.name === event.target.value) });
    // };

    // const validatePaymentMethod = (paymentMethod: string) => {
    //     const paymentMethodValid =
    //         isPayPal || !paymentMethods || paymentMethods.length === 0 || isNonEmptyString(paymentMethod);
    //     setPaymentMethodError(!paymentMethodValid ? translate("Payment Method is required.") : "");
    //     return paymentMethodValid;
    // };

    // const validatePONumber = (poNumber: string) => {
    //     const poNumberValid = !cart || !cart.showPoNumber || !cart.requiresPoNumber || isNonEmptyString(poNumber);
    //     setPONumberError(!poNumberValid ? translate("P.O. No. is required.") : "");
    //     return poNumberValid;
    // };

    // const validateCardHolderName = (cardHolderName: string) => {
    //     const cardHolderNameValid = !isCreditCard || isNonEmptyString(cardHolderName);
    //     setCardHolderNameError(!cardHolderNameValid ? translate("Cardholder name is required.") : "");
    //     return cardHolderNameValid;
    // };

    // const validateCardNumber = (cardNumber: string) => {
    //     let cardNumberEmpty = false;
    //     let cardNumberValid = true;

    //     if (isCreditCard && !useTokenExGateway) {
    //         cardNumberEmpty = !isNonEmptyString(cardNumber);
    //         cardNumberValid = validateCreditCard(cardNumber);
    //     }

    //     if (cardNumberEmpty) {
    //         setCardNumberError(translate("Credit card number is required."));
    //     } else if (!cardNumberValid) {
    //         setCardNumberError(translate("Credit card number is invalid."));
    //     } else {
    //         setCardNumberError("");
    //     }

    //     return { cardNumberEmpty, cardNumberValid };
    // };

    // const validateCardType = (cardType: string) => {
    //     const cardTypeValid = !isCreditCard || useTokenExGateway || (!useTokenExGateway && isNonEmptyString(cardType));
    //     setCardTypeError(!cardTypeValid ? translate("Credit card type is required.") : "");
    //     return cardTypeValid;
    // };

    // const validateCardExpiration = (expirationMonth: number, expirationYear: number) => {
    //     const cardExpired = isCreditCard && isMonthAndYearBeforeToday(expirationMonth, expirationYear);
    //     setExpirationError(cardExpired ? translate("Card is expired. Please enter a valid expiration date.") : "");
    //     return cardExpired;
    // };

    // const validateSecurityCode = (securityCode: string) => {
    //     let securityCodeEmpty = false;
    //     let securityCodeValid = true;

    //     if (isCreditCard && !useTokenExGateway) {
    //         securityCodeEmpty = !isNonEmptyString(securityCode);
    //         securityCodeValid = /^\d+$/.test(securityCode);
    //     }

    //     if (securityCodeEmpty) {
    //         setSecurityCodeError(!securityCodeValid ? translate("Security code is required.") : "");
    //     } else if (!securityCodeValid) {
    //         setSecurityCodeError(translate("Security code is invalid."));
    //     } else {
    //         setSecurityCodeError("");
    //     }

    //     return { securityCodeEmpty, securityCodeValid };
    // };

    // const validateAddress1 = (address1: string) => {
    //     const address1Valid =
    //         !(isCreditCard || isECheck) || useBillingAddress || (!useBillingAddress && isNonEmptyString(address1));
    //     setAddress1Error(!address1Valid ? translate("Address is required.") : "");
    //     return address1Valid;
    // };

    // const validateCountry = (countryId: string) => {
    //     const countryValid =
    //         !(isCreditCard || isECheck) || useBillingAddress || (!useBillingAddress && isNonEmptyString(countryId));
    //     setCountryError(!countryValid ? translate("Country is required.") : "");
    //     return countryValid;
    // };

    // const validateState = (stateId: string) => {
    //     const stateValid =
    //         (!!selectedCountry && !!selectedCountry.states && selectedCountry.states.length <= 0) ||
    //         !(isCreditCard || isECheck) ||
    //         useBillingAddress ||
    //         (!useBillingAddress && isNonEmptyString(stateId));
    //     setStateError(!stateValid ? translate("State is required.") : "");
    //     return stateValid;
    // };

    // const validateCity = (city: string) => {
    //     const cityValid =
    //         !(isCreditCard || isECheck) || useBillingAddress || (!useBillingAddress && isNonEmptyString(city));
    //     setCityError(!cityValid ? translate("City is required.") : "");
    //     return cityValid;
    // };

    // const validatePostalCode = (postalCode: string) => {
    //     const postalCodeValid =
    //         !(isCreditCard || isECheck) || useBillingAddress || (!useBillingAddress && isNonEmptyString(postalCode));
    //     setPostalCodeError(!postalCodeValid ? translate("Postal Code is required.") : "");
    //     return postalCodeValid;
    // };

    const handleAccountNumberIFrame = (accountNumberIFrame: IFrame) => {
        tokenExAccountNumberIframe = accountNumberIFrame;
    };

    const handlePaymetricValidateSuccess = (success: boolean) => {
        if (success) {
            paymetricIframe.submit({
                onSuccess: (msg: string) => {
                    // The HasPassed is case sensitive, and not standard json.
                    const message: { data: { HasPassed: boolean } } = JSON.parse(msg);
                    if (message.data.HasPassed) {
                        handleSuccessSubmitPaymetricIframe();
                    }
                },
                onError: (msg: string) => {
                    const message: { data: { Code: number; HasPasssed: boolean; Message: string } } = JSON.parse(msg); // Code = 150 -> Already submitted
                    if (message.data.Code === 150) {
                        handleSuccessSubmitPaymetricIframe();
                    }
                    if (!message.data.HasPasssed) {
                        if (message.data.Message === "Error" && message.data.Code === 100) {
                            setPlaceOrderErrorMessage({
                                errorMessage: siteMessage("Checkout_Paymetric_Code_100_Error") as string,
                            });
                        } else if (message.data.Message && message.data.Message !== "Error") {
                            setPlaceOrderErrorMessage({
                                 
                                errorMessage: translate(message.data.Message),
                            });
                        }
                    }
                },
            });
        }
    };

    const handleSuccessSubmitPaymetricIframe = () => {
        if (!paymetricConfig?.accessToken) {
            return;
        }

        getPaymetricResponsePacket({
            accessToken: paymetricConfig.accessToken,
            onComplete: result => {
                if (result.apiResult?.success) {
                    setCardType(convertPaymetricCardType(result.apiResult.creditCard.cardType));
                    setExpirationMonth(result.apiResult.creditCard.expirationMonth!);
                    setExpirationYear(result.apiResult.creditCard.expirationYear!);
                    setCardNumber(result.apiResult.creditCard.cardNumber!);
                    setSecurityCode(result.apiResult.creditCard.securityCode!);
                    setCardHolderName(result.apiResult.creditCard.cardHolderName!);
                    setIsCardNumberTokenized(true);
                }
            },
        });
    };

     const validateFormForProvider = (externalProvider: string) => {
        return (
            validatePONumber(poNumber) &&
            validateAddress1(address1) &&
            validateCountry(countryId) &&
            validateState(stateId) &&
            validateCity(city) &&
            validatePostalCode(postalCode) &&
            !requestedDeliveryDateDisabled &&
            !requestedPickUpDateDisabled
        );
    };

    const scrollToFirstFormError = () => {
        if (showFormErrors) {
            const firstErrorElement = document.querySelector("[aria-invalid='true']");
            const header = document.querySelector("header");

            if (firstErrorElement && header) {
                const formFieldParent = firstErrorElement.closest("[class*='FormFieldStyle']");
                if (!formFieldParent) {
                    return;
                }
                const { top } = formFieldParent.getBoundingClientRect();
                window.scrollTo({
                    top: top + window.pageYOffset - header.offsetHeight,
                    left: 0,
                    behavior: "smooth",
                });
            }
        }
    };

    // Side-effect-safe (surfaces field errors, no tokenization) validator used by
    // the wizard to gate Next / jump to this step when the payment details are
    // incomplete - including the credit card, e-check and saved profile fields.
    const validatePaymentFields = async () => {
        if (isZeroTotal) {
            const valid = validatePONumber(poNumber) && !requestedDeliveryDateDisabled && !requestedPickUpDateDisabled;
            if (!valid) {
                setShowFormErrors(true);
            }
            return valid;
        }
        const paymentMethodValid = validatePaymentMethod(paymentMethod);
        const poNumberValid = validatePONumber(poNumber);

        // The CVV entry is not rendered for an expired saved card, so no field validator can catch it.
        if (isPaymentProfileExpired) {
            setShowFormErrors(true);
            return false;
        }

        // PayPal and drop-in gateways capture the card in their own hosted UI and
        // complete on submit; only the base fields can be verified up-front.
        if (isPayPal) {
            if (!paymentMethodValid) {
                setShowFormErrors(true);
            }
            return paymentMethodValid;
        }
        if (useAdyenDropIn && paymentMethodDto?.isCreditCard) {
            if (!(paymentMethodValid && poNumberValid)) {
                setShowFormErrors(true);
            }
            return paymentMethodValid && poNumberValid;
        }

        const address1Valid = validateAddress1(address1);
        const countryValid = validateCountry(countryId);
        const stateValid = validateState(stateId);
        const cityValid = validateCity(city);
        const postalCodeValid = validatePostalCode(postalCode);

        let accountHolderNameValid = true;
        let accountNumberValid = true;
        let routingNumberValid = true;
        if (eCheckDetails?.current) {
            accountHolderNameValid = eCheckDetails.current.validateAccountHolderNameChange(accountHolderName);
            accountNumberValid = eCheckDetails.current.validateAccountNumberChange(accountNumber);
            routingNumberValid = eCheckDetails.current.validateRoutingNumberChange(routingNumber);
        }

        let creditCardValid = true;
        if (creditCardDetails?.current) {
            creditCardValid = await creditCardDetails.current.validate();
        }
        if (savedPaymentProfile?.current) {
            creditCardValid = await savedPaymentProfile.current.validate();
        }

        const valid =
            paymentMethodValid &&
            poNumberValid &&
            address1Valid &&
            countryValid &&
            stateValid &&
            cityValid &&
            postalCodeValid &&
            accountHolderNameValid &&
            accountNumberValid &&
            routingNumberValid &&
            creditCardValid;
        if (!valid) {
            setShowFormErrors(true);
        }
        return valid;
    };
    validatePaymentFieldsRef.current = validatePaymentFields;

    const validateForm = async () => {
        if (isZeroTotal) {
            return validatePaymentFields();
        }
        const paymentMethodValid = validatePaymentMethod(paymentMethod);
        const poNumberValid = validatePONumber(poNumber);

        // The CVV entry is not rendered for an expired saved card, so no field validator can catch it.
        if (isPaymentProfileExpired) {
            setShowFormErrors(true);
            return false;
        }

        if (isPayPal) {
            return paymentMethodValid;
        }

        if (useAdyenDropIn && paymentMethodDto?.isCreditCard) {
            return poNumberValid;
        }

        if (useECheckTokenExGateway && isECheck) {
            tokenExAccountNumberIframe?.validate();
        }

          if (useSpreedlyDropIn && isStripeRadarScriptLoaded && !antiFraudSessionId && !isPayPal) {
            setPlaceOrderErrorMessage({
                errorMessage: translate(
                    "The Stripe Radar session has not been loaded. Please try again in a minute.",
                ) as string,
            });
            return false;
        }

        if (usePaymetricGateway && cart && cart.showCreditCard && !cart.requiresApproval) {
            const isFormValidForPaymetricPayment = paymentMethodValid && poNumberValid;
            if (!isFormValidForPaymetricPayment) {
                return false;
            }
            if (isCreditCard && paymetricIframe) {
                paymetricIframe.validate({
                    onValidate: (success: boolean) => {
                        handlePaymetricValidateSuccess(success);
                    },
                });
            } else if (isPaymentProfile) {
                if (savedPaymentProfile?.current) {
                    return await savedPaymentProfile.current.validate();
                }
                return true;
            }
        }

        if (useSquareGateway && cart && cart.showCreditCard && !cart.requiresApproval) {
            const isFormValidForSquarePayment = paymentMethodValid && poNumberValid;
            if (!isFormValidForSquarePayment) {
                return false;
            }
            if (isCreditCard && squareCard) {
                tokenizeSquareCard();
            } else if (isPaymentProfile) {
                return true;
            }
        }

        if (usePaystandDropIn && isCreditCard && cart && cart.showCreditCard && !cart.requiresApproval) {
            const isTokenExist = payerId && cardNumber;
            setPaystandError(!isTokenExist ? translate("Payment details are required.") : "");
            return isTokenExist;
        }

        const address1Valid = validateAddress1(address1);
        const countryValid = validateCountry(countryId);
        const stateValid = validateState(stateId);
        const cityValid = validateCity(city);
        const postalCodeValid = validatePostalCode(postalCode);
        let accountHolderNameValid = true;
        let accountNumberValid = true;
        let routingNumberValid = true;
        if (eCheckDetails?.current) {
            accountHolderNameValid = eCheckDetails.current.validateAccountHolderNameChange(accountHolderName);
            accountNumberValid = eCheckDetails.current.validateAccountNumberChange(accountNumber);
            routingNumberValid = eCheckDetails.current.validateRoutingNumberChange(routingNumber);
        }
        let creditCardValid = true;
        if (creditCardDetails?.current && externalTokenProvider !== "GooglePay") {
            creditCardValid = await creditCardDetails.current.validate();
        }

        if (savedPaymentProfile?.current) {
            creditCardValid = await savedPaymentProfile.current.validate();
        }

        if (
            !paymentMethodValid ||
            !poNumberValid ||
            !address1Valid ||
            !countryValid ||
            !stateValid ||
            !cityValid ||
            !postalCodeValid
        ) {
            scrollToFirstFormError();
        }

        if (!paymentMethodValid) {
            paymentMethodRef.current?.focus();
        } else if (!poNumberValid) {
            poNumberRef.current?.focus();
        } else if (!address1Valid) {
            address1Ref.current?.focus();
        } else if (!countryValid) {
            countryRef.current?.focus();
        } else if (!stateValid) {
            stateRef.current?.focus();
        } else if (!cityValid) {
            cityRef.current?.focus();
        } else if (!postalCodeValid) {
            postalCodeRef.current?.focus();
        }

        return (
            paymentMethodValid &&
            poNumberValid &&
            creditCardValid &&
            address1Valid &&
            countryValid &&
            stateValid &&
            cityValid &&
            postalCodeValid &&
            accountHolderNameValid &&
            accountNumberValid &&
            routingNumberValid &&
            !requestedDeliveryDateDisabled &&
            !requestedPickUpDateDisabled
        );
    };

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setExternalTokenProvider(null);
        if (document.activeElement?.className === "adyen-checkout__payment-method__header__title") {
            // The adyen form buttons are incorrectly submitting our form, we should just ignore these occurances.
            return false;
        }

        if (!(await validateForm())) {
            setShowFormErrors(true);
            // Submit can be triggered from the review step; surface the payment
            // step so the user sees the validation errors.
            goToStep(CHECKOUT_WIZARD_STEP.Payment);
            return false;
        }

        if (!isZeroTotal && useAdyenDropIn && isCreditCard && !isPayPal) {
            if (adyenDropIn) {
                const { success, errorMessage } = await validateAdyenPayment({
                    customerId: cartState.value?.billToId,
                    paymentAmount: cartState.value?.orderGrandTotal,
                });
                if (success) {
                    window.localStorage.setItem("order-po-number", poNumber);
                    adyenDropIn.submit();
                } else if (errorMessage) {
                    setAdyenErrorMessage(errorMessage);
                }
            }
        } else if (!isZeroTotal && useTokenExGateway && ((isPaymentProfile && !bypassCvvForSavedCards) || isCreditCard)) {
            creditCardDetails.current?.tokenize();
            savedPaymentProfile.current?.tokenize();
        } else if (!isZeroTotal && useSpreedlyDropIn && isCreditCard) {
            creditCardDetails.current?.tokenize();
        } else if (!isZeroTotal && useECheckTokenExGateway && !isPayPal && isECheck) {
            tokenExAccountNumberIframe?.tokenize();
        } else {
            placeOrder({
                paymentMethod,
                poNumber,
                vatNumber,
                saveCard,
                cardHolderName,
                cardNumber,
                cardType,
                expirationMonth,
                expirationYear,
                securityCode,
                useBillingAddress,
                address1,
                countryId,
                stateId,
                city,
                postalCode,
                payPalToken,
                payPalPayerId,
                accountHolderName,
                accountNumber,
                routingNumber,
                payerId,
                maskedCardNumber,
                paymentSplitChoice: effectiveSplitChoice,
                antiFraudSessionId,
                onSuccess: (cartId: string) => {
                    preloadOrderConfirmationData({
                        cartId,
                        onSuccess: () => {
                            if (cart?.isAwaitingApproval) {
                                resetCurrentCartId({});
                                toaster.addToast({
                                    body: siteMessage("OrderApproval_OrderPlaced"),
                                    messageType: "success",
                                });
                            }
                            history.push(`${orderConfirmationPageLink!.url}?cartId=${cartId}`);
                        },
                    });
                },
                onComplete(resultProps: any) {
                    if (!resultProps.apiResult?.cart) {
                        return;
                    }
                    // "this" is targeting the object being created, not the parent SFC
                     
                    this.onSuccess?.(resultProps.apiResult.cart.id);
                },
            } as PlaceOrderParameter);
        }

        return false;
    };

    const submitPayPalRequest = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        if (!checkoutReviewAndSubmitPageLink) {
            return;
        }
        if (!session?.isAuthenticated) {
            return;
        }
        setPaymentMethod("");
        setRunSubmitPayPal(true);
        setPayPalError("");
        setIsPayPal(true);
    };

    const handleExternalProviderValidate = (externalProvider: "GooglePay" | "ApplePay"): boolean => {
        if (!checkoutReviewAndSubmitPageLink) {
            return false;
        }
        if (!session?.isAuthenticated) {
            return false;
        }

        setExternalTokenProvider(externalProvider);
        const isValid = validateFormForProvider(externalProvider);
        if (!isValid) {
            setShowFormErrors(true);
            return false;
        }

        return true;
    };

    const handleGooglePayAuthorize = (paymentData: google.payments.api.PaymentData) => {
        setCardNumber(paymentData.paymentMethodData?.tokenizationData?.token || "");
        setCreditCardLastFourDigits(paymentData.paymentMethodData?.info?.cardDetails || "");
        setMaskedCardNumber(paymentData.paymentMethodData?.info?.cardDetails || "");
        setCardType(paymentData.paymentMethodData?.info?.cardNetwork || "");
        setPaymentMethod("GooglePay");
        setSecurityCode("GooglePay");
    };

    const handleApplePayAuthorize = (paymentData: any) => {
        const tokenData = JSON.stringify(paymentData.paymentData);
        setCardNumber(tokenData);
        setCreditCardLastFourDigits(paymentData.paymentMethod?.displayName?.split(" ").pop() || "");
        setMaskedCardNumber(paymentData.paymentMethod?.displayName || "ApplePay");
        setCardType(paymentData.paymentMethod?.network || "ApplePay");
        setPaymentMethod("ApplePay");
        setSecurityCode("ApplePay");
    };

    const handleResetPaymentMethodClick = () => {
        if (usePaystandDropIn) {
            paystandCard.resetCheckout(billToState.value!);
        }
    };

    if (!tokenExFrameStyleConfig) {
        tokenExFrameStyleConfig = generateTokenExFrameStyleConfig({ theme });
    }

    if (!spreedlyFrameStyleConfig && useSpreedlyDropIn) {
        spreedlyFrameStyleConfig = generateSpreedlyFrameStyleConfig({ theme });
    }

    if (
        !cart ||
        cart.requiresApproval ||
        !paymentOptions ||
        !orderConfirmationPageLink ||
        (!isZeroTotal && useTokenExGateway && !tokenExLibraryIsLoaded) ||
        (!isZeroTotal && useSpreedlyDropIn && !spreedlyLibraryIsLoaded)
    ) {
        return null;
    }

    const iframeName = useTokenExGateway
        ? "TokenEx"
        : usePaymetricGateway
        ? "Paymetric"
        : useSquareGateway
        ? "Square"
        : usePaystandDropIn
        ? "Paystand"
        : useSpreedlyDropIn
        ? "Spreedly"
        : undefined;

    const paymentDetailsStyles = usePaystandDropIn ? styles.paystandDetailsGridItem : styles.creditCardDetailsGridItem;

    return (
        <StyledForm
            {...styles.form}
            id="reviewAndSubmitPaymentForm"
            onSubmit={handleFormSubmit}
            noValidate={true}
            data-test-selector="reviewAndSubmitPaymentForm"
        >
            {/* This button should only be used to trigger the submit of the form. Should be on the top of the form. */}
            <button id="reviewAndSubmitPaymentForm-submit" type="submit" style={{ display: "none" }}>
                {translate("Place Order")}
            </button>
            {!isMgbwWebsite() && <CheckoutBillingAddressCard />}
            {isZeroTotal && (
                <>
                    <CheckoutSectionHeader title={translate("Payment Information")} />
                    <StyledFieldSet {...styles.fieldset}>
                        <GridItem {...styles.cardContentGridItem}>
                            <StyledWrapper {...styles.cardContent}>
                                <GridContainer {...styles.paymentMethodAndPONumberContainer}>
                                    <GridItem
                                        {...styles.paymentMethodGridItem}
                                        width={cart.showPoNumber ? 6 : 12}
                                    >
                                        <StyledWrapper {...styles.termsPaymentMethod}>
                                            <Typography {...styles.termsPaymentMethodLabel}>
                                                {translate("Payment Method")}
                                            </Typography>
                                            <Typography {...styles.termsPaymentMethodValue}>
                                                {translate("No payment needed")}
                                            </Typography>
                                            <Typography {...styles.termsPaymentMethodHelper}>
                                                {translate("Your order total is $0.00.")}
                                            </Typography>
                                        </StyledWrapper>
                                    </GridItem>
                                    {enableVat && (
                                        <GridItem {...styles.vatNumberGridItem}>
                                            <TextField
                                                {...styles.vatNumberText}
                                                label={translate("VAT Number")}
                                                value={vatNumber}
                                                onChange={handleVatNumberChange}
                                                maxLength={50}
                                                data-test-selector="checkoutReviewAndSubmit_vatNumber"
                                            />
                                        </GridItem>
                                    )}
                                    {cart.showPoNumber && (
                                        <GridItem {...styles.poNumberGridItem}>
                                            <TextField
                                                {...styles.poNumberText}
                                                label={
                                                    <>
                                                        <span aria-hidden>{translate("P.O. No.")}</span>
                                                        <VisuallyHidden>{translate("Purchase Order Number")}</VisuallyHidden>
                                                    </>
                                                }
                                                value={poNumber}
                                                onChange={handlePONumberChange}
                                                required={poRequired}
                                                maxLength={30}
                                                error={showFormErrors && poNumberError}
                                                data-test-selector="checkoutReviewAndSubmit_poNumber"
                                                ref={poNumberRef}
                                            />
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </StyledWrapper>
                        </GridItem>
                    </StyledFieldSet>
                </>
            )}
            {!isZeroTotal && <>
            {!isUkWebsite && breakdownModel.scenario !== "allCreditCard" && (
                <StyledWrapper css={css`display: block; margin-bottom: 24px;`}>
                    <CheckoutSectionHeader title={translate("Payment Options")} />
                    <SurPaymentBreakdown
                        cart={cart}
                        userHasTerms={userHasTerms}
                        userHasMgbwTerms={userHasMgbwTerms}
                        shippingPromotionTotal={shippingPromotionTotal}
                        selectedChoice={paymentSplitChoice}
                        onChoiceChange={setPaymentSplitChoice}
                    />
                </StyledWrapper>
            )}
            <CheckoutSectionHeader
                title={translate(isPayOnTerms ? "Payment Information" : "Credit Card Information")}
            />
            <StyledFieldSet {...styles.fieldset}>
                <GridItem {...styles.cardContentGridItem}>
                    <StyledWrapper {...styles.cardContent}>
                        {(paymentMethod === "GooglePay" || paymentMethod === "ApplePay") && (
                        <Typography
                            as="p"
                            {...styles.externalProviderCreditCardInfoText}
                            data-test-selector="externalProviderCreditCardInfoText"
                        >
                            {paymentMethod}&nbsp;
                            {`****${creditCardLastFourDigits}`}&nbsp;
                        </Typography>
                    )}
                        {isPayPal && (
                            <Typography {...styles.paymentMethodPayPalText} as="span">
                                {translate("Selected Payment Method: PayPal")}
                            </Typography>
                        )}
                        {!isPayPal && !(paymentMethod === "GooglePay" || paymentMethod === "ApplePay") && (
                            <GridContainer {...styles.paymentMethodAndPONumberContainer}>
                                <GridItem
                                    {...styles.paymentMethodGridItem}
                                    width={isPayOnTerms && !cart.showPoNumber ? 12 : 6}
                                >
                                    {isPayOnTerms && termsMethod && (
                                        <StyledWrapper {...styles.termsPaymentMethod}>
                                            <Typography {...styles.termsPaymentMethodLabel}>
                                                {translate("Payment Method")}
                                            </Typography>
                                            <Typography {...styles.termsPaymentMethodValue}>
                                                {termsMethod.description || termsMethod.name}
                                            </Typography>
                                            {!isUkWebsite && (
                                                <Typography {...styles.termsPaymentMethodHelper}>
                                                    {translate("Your order will be billed to your account.")}
                                                </Typography>
                                            )}
                                        </StyledWrapper>
                                    )}
                                    {!isPayOnTerms && visiblePaymentMethods && visiblePaymentMethods.length > 0 && (
                                        <>
                                            <Select
                                                {...styles.paymentMethodSelect}
                                                label={translate("Payment Method")}
                                                value={paymentMethod ?? paymentMethodDto?.name}
                                                onChange={handlePaymentMethodChange}
                                                required
                                                error={showFormErrors && paymentMethodError}
                                                data-test-selector="checkoutReviewAndSubmit_paymentMethod"
                                                ref={paymentMethodRef}
                                            >
                                                <option value="">{translate("Select Payment Method")}</option>
                                                {visiblePaymentMethods.map((method, index) => (
                                                    <option key={`${method.name}-${index}`} value={method.name}>
                                                        {method.description || method.name}
                                                    </option>
                                                ))}
                                            </Select>
                                            {paymentMethodDto?.isPaymentProfile && !paymentMethodDto.isPaymentProfileExpired && (
                                                <StyledWrapper {...styles.paymentBilling}>
                                                    <PaymentProfileBillingAddress
                                                        address={paymentMethodDto.billingAddress}
                                                        extendedStyles={styles.paymentProfileBillingAddress}
                                                    />
                                                </StyledWrapper>
                                            )}
                                            {paymentMethodDto?.isPaymentProfile && paymentMethodDto.isPaymentProfileExpired && (
                                                <StyledWrapper {...styles.paymentProfileExpiredErrorWrapper}>
                                                    <Typography {...styles.paymentProfileExpiredErrorText}>
                                                        {siteMessage("Checkout_PaymentProfileExpired")}
                                                    </Typography>
                                                    {savedPaymentsPageLink && (
                                                        <Link
                                                            {...styles.paymentProfileEditCardLink}
                                                            onClick={handleEditCardClick}
                                                        >
                                                            {translate("Edit Card")}
                                                        </Link>
                                                    )}
                                                </StyledWrapper>
                                            )}
                                        </>
                                    )}
                                    {showPayPal && (
                                        <PayPalButton
                                            submitPayPalRequest={submitPayPalRequest}
                                            extendedStyles={styles.payPalButton}
                                            error={payPalError}
                                        ></PayPalButton>
                                    )}
                                     {(enableGooglePay || enableApplePay) && (
                                <StyledWrapper {...styles.externalPayButtonsContainer}>
                                    {enableGooglePay && spreedlyConfig && (
                                        <GooglePayButtonWrapper
                                            amount={cart.orderGrandTotal!}
                                            currencyCode={currency?.currencyCode ?? ""}
                                            gatewayMerchantId={spreedlyConfig?.environmentKey || ""}
                                            onValidate={() => handleExternalProviderValidate("GooglePay")}
                                            onSuccess={paymentData => handleGooglePayAuthorize(paymentData)}
                                            onError={(error: string) => setGooglePayError(error)}
                                            error={showFormErrors ? googlePayError : undefined}
                                            extendedStyles={styles.googlePayButtonWrapperStyles}
                                        />
                                    )}
                                    {enableApplePay && (
                                        <ApplePayButtonWrapper
                                            amount={cart.orderGrandTotal!}
                                            countryCode={selectedCountry?.abbreviation ?? "US"}
                                            currencyCode={currency?.currencyCode ?? ""}
                                            onValidate={() => handleExternalProviderValidate("ApplePay")}
                                            onSuccess={paymentData => handleApplePayAuthorize(paymentData)}
                                            onError={(error: string) => setApplePayError(error)}
                                            error={showFormErrors ? applePayError : undefined}
                                            extendedStyles={styles.applePayButtonWrapperStyles}
                                        />
                                    )}
                                </StyledWrapper>
                            )}
                                </GridItem>
                                {enableVat && (
                                    <GridItem {...styles.vatNumberGridItem}>
                                        <TextField
                                            {...styles.vatNumberText}
                                            label={translate("VAT Number")}
                                            value={vatNumber}
                                            onChange={handleVatNumberChange}
                                            maxLength={50}
                                            data-test-selector="checkoutReviewAndSubmit_vatNumber"
                                        />
                                    </GridItem>
                                )}
                                {cart.showPoNumber && (
                                    <GridItem {...styles.poNumberGridItem}>
                                        <TextField
                                            {...styles.poNumberText}
                                            label={
                                                <>
                                                    <span aria-hidden>{translate("P.O. No.")}</span>
                                                    <VisuallyHidden>{translate("Purchase Order Number")}</VisuallyHidden>
                                                </>
                                            }
                                            value={poNumber}
                                            onChange={handlePONumberChange}
                                            // required={cart.requiresPoNumber}
                                            // required={!(paymentMethod === "Credit Card" || /^\d{16}$/.test(paymentMethod))}
                                            required={poRequired}
                                            maxLength={30}
                                            error={showFormErrors && poNumberError}
                                            data-test-selector="checkoutReviewAndSubmit_poNumber"
                                            ref={poNumberRef}
                                        />
                                    </GridItem>
                                )}
                                {cart.showPoNumber && enableVat && <GridItem {...styles.emptyGridItem}></GridItem>}
                                {paymentMethodDto?.isPaymentProfile &&
                                    !paymentMethodDto.isPaymentProfileExpired &&
                                    !bypassCvvForSavedCards && (
                                        <GridItem width={6}>
                                            <SavedPaymentProfileEntry
                                                iframe={iframeName}
                                                onSecurityCodeUpdate={handleSecurityCodeChange}
                                                tokenExConfig={tokenExConfig}
                                                tokenExFrameStyleConfig={tokenExFrameStyleConfig}
                                                paymentMethodDto={paymentMethodDto}
                                                updateCardIsTokenized={setIsCardNumberTokenized}
                                                ref={savedPaymentProfile}
                                                extendedStyles={styles.savedPaymentProfile}
                                            />
                                        </GridItem>
                                    )}
                                {paymentMethodDto?.isCreditCard && useAdyenDropIn && !adyenDropIn && !showPayPal && (
                                    <LoadingSpinner {...styles.loadingSpinner} />
                                )}
                                {paymentMethodDto?.isCreditCard && useAdyenDropIn && (
                                    <GridItem {...styles.adyenDropInGridItem}>
                                        <AdyenDropIn
                                            redirectResult={adyenRedirectResult}
                                            adyenConfiguration={adyenConfiguration}
                                            setAdyenDropIn={setAdyenDropIn}
                                            adyenErrorMessage={adyenErrorMessage}
                                        />
                                    </GridItem>
                                )}
                                {cart.showCreditCard && paymentMethodDto?.isCreditCard && !useAdyenDropIn && (
                                    <GridItem {...paymentDetailsStyles}>
                                        <CreditCardDetailsEntry
                                            canSaveCard={paymentOptions.canStorePaymentProfile}
                                            iframe={iframeName}
                                            paymetricFrameRef={paymetricFrameRef}
                                            onSaveCardUpdate={handleSaveCardChange}
                                            onCardHolderNameUpdate={handleCardHolderNameChange}
                                            onCardNumberUpdate={handleCardNumberChange}
                                            onCardTypeUpdate={handleCardTypeChange}
                                            onExpirationMonthUpdate={handleExpirationMonthChange}
                                            onExpirationYearUpdate={handleExpirationYearChange}
                                            onSecurityCodeUpdate={handleSecurityCodeChange}
                                            availableCardTypes={paymentOptions.cardTypes ?? []}
                                            availableMonths={paymentOptions.expirationMonths ?? []}
                                            availableYears={paymentOptions.expirationYears ?? []}
                                            extendedStyles={styles.creditCardDetails}
                                            onResetPaymentMethodClick={handleResetPaymentMethodClick}
                                            isIframeLoaded={isIframeLoaded}
                                            error={showFormErrors ? paystandError : undefined}
                                            bankAccountLastFourDigits={bankAccountLastFourDigits}
                                            creditCardLastFourDigits={creditCardLastFourDigits}
                                            tokenExConfig={tokenExConfig}
                                            spreedlyConfig={spreedlyConfig}
                                            tokenExFrameStyleConfig={tokenExFrameStyleConfig}
                                            spreedlyFrameStyleConfig={spreedlyFrameStyleConfig}
                                            updateCardIsTokenized={setIsCardNumberTokenized}
                                            updateMaskedCardNumber={setMaskedCardNumber}
                                            ref={creditCardDetails}
                                        />
                                    </GridItem>
                                )}
                                {cart.showECheck && paymentMethodDto?.isECheck && (
                                    <GridItem {...styles.eCheckDetailsGridItem}>
                                        <ECheckDetailsEntry
                                            iframe={useECheckTokenExGateway ? "TokenEx" : undefined}
                                            paymentMethod={paymentMethod}
                                            onAccountHolderNameChange={setAccountHolderName}
                                            onAccountNumberChange={setAccountNumber}
                                            onRoutingNumberChange={setRoutingNumber}
                                            tokenExConfig={tokenExConfig}
                                            tokenExFrameStyleConfig={tokenExFrameStyleConfig}
                                            extendedStyles={styles.eCheckDetailsEntryStyles}
                                            showFormErrors={showFormErrors}
                                            updateIsECheckTokenized={setIsECheckTokenized}
                                            updateShowFormErrors={setShowFormErrors}
                                            setAccountNumberIFrame={handleAccountNumberIFrame}
                                            ref={eCheckDetails}
                                        />
                                    </GridItem>
                                )}
                                {((cart.showECheck && paymentMethodDto?.isECheck) ||
                                    (cart.showCreditCard &&
                                        paymentMethodDto?.isCreditCard &&
                                        !useAdyenDropIn &&
                                        !usePaystandDropIn)) && (
                                    <GridItem {...styles.creditCardAddressGridItem}>
                                        <CreditCardBillingAddressEntry
                                            useBillTo={useBillingAddress}
                                            onUseBillToChange={handleUseBillingAddressChange}
                                            billTo={billToState.value}
                                            address1={address1}
                                            address1Ref={address1Ref}
                                            onAddress1Change={handleAddressChange}
                                            address1Error={showFormErrors ? address1Error : undefined}
                                            country={countryId}
                                            countryRef={countryRef}
                                            onCountryChange={handleCountryChange}
                                            countryError={showFormErrors ? countryError : undefined}
                                            state={stateId}
                                            stateRef={stateRef}
                                            onStateChange={handleStateChange}
                                            stateError={showFormErrors ? stateError : undefined}
                                            city={city}
                                            cityRef={cityRef}
                                            onCityChange={handleCityChange}
                                            cityError={showFormErrors ? cityError : undefined}
                                            postalCode={postalCode}
                                            postalCodeRef={postalCodeRef}
                                            onPostalCodeChange={handlePostalCodeChange}
                                            postalCodeError={showFormErrors ? postalCodeError : undefined}
                                            availableCountries={countries ?? []}
                                            availableStates={selectedCountry?.states}
                                            extendedStyles={styles.creditCardAddress}
                                        />
                                    </GridItem>
                                )}
                            </GridContainer>
                        )}
                    </StyledWrapper>
                </GridItem>
            </StyledFieldSet>
            </>}
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withToaster(withHistory(withTheme(CheckoutReviewAndSubmitPaymentDetails)))),
    definition: {
        group: "Checkout - Review & Submit",
        displayName: "Payment Details",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
    },
};

export default widgetModule;
